import { Col, Form, Row } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';

export interface TaskDueDateFieldsProps {
    multiDay: boolean;
    withTime: boolean;
    dateStart: number;
    dateEnd: number;
    endTime: string;
    onMultiDayChange: (checked: boolean) => void;
    onWithTimeChange: (checked: boolean) => void;
    onStartDateChange: (isoDate: string) => void;
    onEndDateChange: (isoDate: string) => void;
    onEndTimeChange: (time: string) => void;
    /** Префікс для id перемикачів (уникає колізій при кількох формах) */
    idPrefix?: string;
    hideLabel?: boolean;
}

const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value, { zone: 'local' }).toISODate() || '';
};

const todayIsoDate = (): string => DT.now().setZone('local').toISODate() || '';

const TaskDueDateFields = ({
    multiDay,
    withTime,
    dateStart,
    dateEnd,
    endTime,
    onMultiDayChange,
    onWithTimeChange,
    onStartDateChange,
    onEndDateChange,
    onEndTimeChange,
    idPrefix = 'task',
    hideLabel = false,
}: TaskDueDateFieldsProps) => {
    const { t } = useTranslation(['Tasks']);
    const multiDayId = `${idPrefix}-multi-day`;
    const withTimeId = `${idPrefix}-with-time`;
    const todayIso = todayIsoDate();
    const startIso = millisToIsoDate(dateStart);
    const endIso = millisToIsoDate(dateEnd);
    const endMinIso = multiDay ? startIso || todayIso : todayIso;

    return (
        <>
            <div className="mb-2">
                {!hideLabel && (
                    <label className="form-label text-muted mb-2">{t('labelDueDate')}</label>
                )}
                <div className="d-flex flex-nowrap align-items-center gap-3 mb-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id={multiDayId}
                            checked={Boolean(multiDay)}
                            onChange={(e) => onMultiDayChange(e.target.checked)}
                        />
                        <label htmlFor={multiDayId} className="form-check-label text-nowrap">
                            {t('labelMultiDay')}
                        </label>
                    </div>

                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id={withTimeId}
                            checked={Boolean(withTime)}
                            onChange={(e) => onWithTimeChange(e.target.checked)}
                        />
                        <label htmlFor={withTimeId} className="form-check-label text-nowrap">
                            {t('labelWithTime')}
                        </label>
                    </div>
                </div>
            </div>

            <Row>
                {multiDay && (
                    <Col sm={withTime ? 4 : 6}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('labelDateStart')}</label>
                            <input
                                className="form-control"
                                type="date"
                                required
                                value={startIso}
                                min={todayIso}
                                onChange={(e) => onStartDateChange(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                )}

                <Col sm={multiDay ? (withTime ? 4 : 6) : withTime ? 8 : 12}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('labelDateEnd')}</label>
                        <input
                            className="form-control"
                            type="date"
                            required
                            value={endIso}
                            min={endMinIso || undefined}
                            onChange={(e) => onEndDateChange(e.target.value)}
                        />
                    </Form.Group>
                </Col>

                {withTime && (
                    <Col sm={4}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('labelTime')}</label>
                            <input
                                className="form-control"
                                type="time"
                                required
                                value={endTime || '18:00'}
                                onChange={(e) => onEndTimeChange(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                )}
            </Row>
        </>
    );
};

export default TaskDueDateFields;
